import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';

export type CheckboxProps = React.PropsWithChildren<{
  id?: string,
  text?: string,
  value: boolean,
  onChange: (newVal: boolean) => void,
  disabled?: boolean,
  className?: string,
  style?: React.CSSProperties,
  readonly?: boolean,
  size?: number
} & Omit<React.ComponentPropsWithoutRef<'input'>, 'value'>>

const Checkbox = forwardRef<HTMLDivElement, CheckboxProps>(({
  id, text, value, onChange, readonly, size,
  disabled, className, style, children, ...inputProps
}, ref) => {
  const handleClick = () => {
    onChange(!value);
  };

  return (
    <Shrinkwrap>
      <Container
        id={id}
        onClick={() => { handleClick(); }}
        disabled={disabled}
        className={className}
        style={style}
        ref={ref}
        readonly={readonly}
      >
        <HiddenInput
          {...inputProps}
          type="checkbox"
          value={String(value)}
          onChange={onChange}
        />
        <CustomCheckbox checked={value} size={size}/>
        <CheckboxContent>
          {text ?? children}
        </CheckboxContent>
      </Container>
    </Shrinkwrap>
  );
});

Checkbox.displayName = 'Checkbox';

export default Checkbox;

const Shrinkwrap = styled.div`
  display: table;
`;

const Container = styled.div<{ disabled?: boolean, readonly?: boolean }>`
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  ${({ disabled }) => disabled && css`
    cursor: default;
    opacity: 0.5;
    pointer-events: none;
  `}
  ${({ readonly }) => readonly && css`
    cursor: default;
    pointer-events: none;
  `}
`;

const HiddenInput = styled.input`
  display: none;
`;

const CustomCheckbox = styled.div<{
  checked?: boolean;
  size?: number;
}>`
  position: relative;
  flex: 0 0 auto;
  width: ${({ size = 29 }) => size}px;
  height: ${({ size = 29 }) => size}px;
  background-color: transparent;
  border: solid ${({ theme }) => theme.colors.grey90} 1px;
  transition: background-color 300ms ease-out;

  &:after {
    content: "";
    display: block;
    position: absolute;
    opacity: 0;
    border: solid ${({ theme }) => theme.colors.white};
    border-width: 0 ${({ size = 29 }) => size * 0.1}px ${({ size = 29 }) => size * 0.1}px 0;
    transform: rotate(40deg);
    transition: all 0ms;
  }

  ${({ checked, size = 29, theme }) =>
    checked
    && css`
      background-color: ${theme.colors.primary};
      border: solid ${theme.colors.primary} 1px;

      &:after {
        opacity: 1;
        width: ${size * 0.28}px;
        height: ${size * 0.58}px;
        left: ${size * 0.34}px;
        top: ${size * 0.1}px;
        transition: all 300ms ease-out;
      }
    `}
`;

const CheckboxContent = styled.div`
  ${({ children }) => React.Children.count(children) > 0 && css`
    margin-left: 10px;
    margin-right: 10px;
  `}
`;
